/**
 * <slider-autoplay data-speed="8">
 *   ...<slider-component>...</slider-component>...
 * </slider-autoplay>
 *
 * Advances a plain <slider-component> on an interval, looping back to the first
 * slide at the end. Pauses on hover/focus and when the tab is hidden, and stays
 * off entirely for users who prefer reduced motion.
 */
class SliderAutoplay extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('slider-component');
    if (!this.slider) return;

    this.speed = (parseFloat(this.dataset.speed) || 8) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseenter', this.pause.bind(this));
    this.addEventListener('mouseleave', this.play.bind(this));
    this.addEventListener('focusin', this.pause.bind(this));
    this.addEventListener('focusout', this.play.bind(this));
    this.addEventListener('click', this.restart.bind(this));
    this.addEventListener('touchstart', this.restart.bind(this), { passive: true });

    this.visibilityHandler = () => (document.hidden ? this.pause() : this.play());
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.reducedMotion.addEventListener('change', this.visibilityHandler);

    this.play();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.reducedMotion?.removeEventListener('change', this.visibilityHandler);
  }

  play() {
    this.pause();
    if (this.reducedMotion.matches || document.hidden) return;
    this.interval = setInterval(this.advance.bind(this), this.speed);
  }

  pause() {
    clearInterval(this.interval);
  }

  restart() {
    this.play();
  }

  advance() {
    const slider = this.slider;
    if (slider.hasAttribute('data-destroyed') || slider.totalPages <= 1) return;

    if (slider.currentPage >= slider.totalPages) {
      slider.currentPage = 1;
      slider.jumpToSlide(slider.sliderItems[0]);
    } else {
      slider.calculateFromDirection('next');
    }
  }
}

customElements.define('slider-autoplay', SliderAutoplay);
